import { ReactElement, useCallback, useEffect, useRef } from "react";
import { TextField } from "@fluentui/react";
import { localize } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { validateRequiredTextField } from "src/utils";
import Label from "src/ui/components/Label/Label";
import TinyEditorComponent from "src/ui/components/Tiny/TinyEditorComponent";
import { FormControl } from "../../Forms";
import { UberContentColumn as ContentColumn } from "../UberContent.types";
import "./ExpandableFieldForm.scss";

export type ExpandableFieldFormProps = {
    column:                 ContentColumn;
    enableFieldValidation?: boolean | undefined;
    onColumnChanged:        (tmpId: string, callback: (column: ContentColumn) => ContentColumn) => void;
}

type ExpandableFieldContent = {
    title: string;
    text: string;
}

const deserializeExpandableFieldContent = (json: string | undefined): ExpandableFieldContent => {
    if (!json) {
        return { title: "", text: "" };
    }

    try {
        return JSON.parse(json);
    } catch {
        return { title: "", text: "" };
    }
};

export function ExpandableFieldForm(props: ExpandableFieldFormProps): ReactElement {
    const { column, onColumnChanged } = props;
    const { tmpId } = column;
    const formRef = useRef<HTMLDivElement>(null);
    const content = deserializeExpandableFieldContent(column.content);

    const updateContent = useCallback(
        (partial: Partial<ExpandableFieldContent>) => onColumnChanged(
            tmpId,
            (column) => ({
                ...column,
                content: JSON.stringify({
                    ...deserializeExpandableFieldContent(column.content),
                    ...partial,
                }),
            }),
        ),
        [tmpId, onColumnChanged],
    );

    const onTitleChanged = useCallback(
        (_: unknown, title?: string) => updateContent({ title: title ?? "" }),
        [updateContent],
    );

    const onTextChanged = useCallback(
        (text: string) => updateContent({ text }),
        [updateContent],
    );

    useEffect(() => {
        const onMouseDown = (event: MouseEvent): void => {
            const target = event.target as HTMLElement;
            const isClickOnScrollBar = target.className === "contentWrap" &&
                (target.clientWidth - event.x) < 6;

            if (isClickOnScrollBar ||
                !formRef.current ||
                formRef.current.contains(target) ||
                !document.getElementsByClassName("appWrap")[0]?.contains(target)) {
                return;
            }

            onColumnChanged(tmpId, (column) => ({
                ...column,
                state: SpintrTypes.UberContentSectionState.IsDisplayed,
            }));
        };

        document.addEventListener("mousedown", onMouseDown);

        return () => document.removeEventListener("mousedown", onMouseDown);
    }, [tmpId, onColumnChanged]);

    return (
        <div
            className="EditableContentSectionsRow-IframeForm ExpandableFieldForm"
            ref={formRef}
        >
            <FormControl>
                <TextField
                    label={localize("Rubrik")}
                    defaultValue={content.title}
                    autoFocus
                    required
                    aria-required
                    validateOnFocusIn
                    validateOnFocusOut
                    validateOnLoad={!!props.enableFieldValidation}
                    onGetErrorMessage={validateRequiredTextField}
                    onChange={onTitleChanged}
                />
            </FormControl>
            <Label className="ExpandableFieldForm-contentLabel" color="mid-grey" weight="medium" size="body-2">{localize("Innehall")}</Label>
            <TinyEditorComponent
                required
                id={"EditableContentSectionsRow" + tmpId}
                content={content.text}
                saveOnClickOutside={false}
                onChange={onTextChanged}
            />
        </div>
    );
}
